import { useState, useEffect, useRef } from 'react';

interface FilterSectionProps {
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
}

export default function FilterSection({ 
  selectedCategory, 
  onCategoryChange, 
  searchQuery, 
  onSearchChange 
}: FilterSectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  const categories = [
    { name: 'All Articles', icon: 'ri-book-line', count: 18 },
    { name: 'AI Strategy & Implementation', icon: 'ri-brain-line', count: 6 },
    { name: 'Advanced Growth Marketing', icon: 'ri-line-chart-line', count: 4 },
    { name: 'Brand Evolution & Design Theory', icon: 'ri-palette-line', count: 3 },
    { name: 'Web3 & Future Tech Explored', icon: 'ri-rocket-line', count: 3 },
    { name: 'Practical Guides & Tutorials', icon: 'ri-tools-line', count: 2 }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      id="knowledge-base"
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, #f97316 0%, transparent 50%), 
                           radial-gradient(circle at 80% 70%, #ef4444 0%, transparent 50%)`
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Explore Our Knowledge Base
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Dive into our extensive collection of articles. Use the categories and search bar to find specific topics that matter most to your digital journey.
          </p>
        </div>

        {/* Search Bar */}
        <div className={`mb-12 transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="max-w-2xl mx-auto relative">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-200 rounded-2xl focus:border-orange-500 focus:outline-none transition-colors duration-300 bg-white shadow-lg"
              />
            </div>
          </div>
        </div>

        {/* Filter Categories */}
        <div className={`transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {categories.map((category, index) => (
              <button
                key={category.name}
                onClick={() => onCategoryChange(category.name)}
                className={`group p-6 rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 cursor-pointer ${
                  selectedCategory === category.name
                    ? 'bg-gradient-to-r from-orange-500 to-red-500 border-orange-500 text-white shadow-xl'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-orange-300 hover:shadow-lg'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    selectedCategory === category.name
                      ? 'bg-white/20'
                      : 'bg-gradient-to-r from-orange-100 to-red-100'
                  }`}>
                    <i className={`${category.icon} text-xl ${
                      selectedCategory === category.name ? 'text-white' : 'text-orange-600'
                    }`}></i>
                  </div>
                  <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                    selectedCategory === category.name
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {category.count}
                  </span>
                </div>
                <h3 className="font-semibold text-lg mb-2">{category.name}</h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm opacity-80">View articles</span>
                  <i className={`ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300 ${
                    selectedCategory === category.name ? 'text-white' : 'text-orange-600'
                  }`}></i>
                </div>
              </button>
            ))}
          </div>

          {/* Active Filter Display */}
          <div className="flex items-center justify-center gap-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <span className="text-gray-600">Currently viewing:</span>
            <div className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-full">
              <i className={categories.find(c => c.name === selectedCategory)?.icon || 'ri-book-line'}></i>
              <span className="font-semibold">{selectedCategory}</span>
            </div>
            {searchQuery && (
              <div className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full">
                <i className="ri-search-line"></i>
                <span className="font-medium">"{searchQuery}"</span>
                <button 
                  onClick={() => onSearchChange('')}
                  className="ml-1 text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}